/**
 * Guard de trazabilidad MAPA: antes de ejecutar acciones que cambian la
 * configuración activa (logout, cambio de ruta, cambio de cisterna…) verifica
 * que no haya datos pendientes de subir. Si los hay, abre un diálogo y solo
 * ejecuta la acción si el conductor envía o cancela.
 *
 * Uso:
 *   import { useMapaGuard } from '@/composables/useMapaGuard'
 *   const { attempt } = useMapaGuard()
 *   attempt('logout', async () => { await store.dispatch('auth/logout') })
 */
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

// Singleton: estado compartido entre AppMapaGuard.vue (renderiza el dialog)
// y las vistas que invocan attempt(). Definido fuera de useMapaGuard para
// que TODAS las llamadas compartan el mismo ref reactivo.
const guardOpen = ref(false)
const guardKind = ref('')
const guardSending = ref(false)
let guardPendingFn = null

export function useMapaGuard() {
  const store = useStore()
  const pendingCount = computed(() => store.getters['recogida/pendingCount'] || 0)

  /**
   * @param {string} kind  Etiqueta humana ("logout", "ruta", "cisterna"…).
   * @param {Function} fn  Acción a ejecutar si no hay pendientes o si el
   *                       conductor pulsa "Enviar ahora" y el upload sale OK.
   */
  function attempt(kind, fn) {
    if (pendingCount.value === 0) {
      fn()
      return
    }
    guardKind.value = kind
    guardPendingFn = fn
    guardOpen.value = true
  }

  async function sendNow() {
    guardSending.value = true
    try {
      await store.dispatch('recogida/upload')
      const pending = guardPendingFn
      guardPendingFn = null
      guardOpen.value = false
      if (pending) await pending()
    } catch (e) {
      // El catch en el store ya lanza mensaje; aquí dejamos el dialog abierto
      // para que el conductor reintente o cancele.
      console.error('MAPA guard sendNow error', e)
    } finally {
      guardSending.value = false
    }
  }

  function cancel() {
    guardPendingFn = null
    guardOpen.value = false
  }

  return { guardOpen, guardKind, guardSending, pendingCount, attempt, sendNow, cancel }
}
